import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Check, Loader2, PenLine, Eraser } from "lucide-react";

export default function SignContract() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/contracts/sign/${token}`)
      .then((r) => setContract(r.data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not load contract"))
      .finally(() => setLoading(false));
  }, [token]);

  const canvasRef = useRef(null);
  const [hasDrawn, setHasDrawn] = useState(false);

  function getCanvasCtx() {
    const c = canvasRef.current;
    if (!c) return null;
    return c.getContext("2d");
  }

  function startDraw(e) {
    const ctx = getCanvasCtx();
    if (!ctx) return;
    ctx.beginPath();
    const { x, y } = getPos(e);
    ctx.moveTo(x, y);
    canvasRef.current._drawing = true;
  }
  function draw(e) {
    const c = canvasRef.current;
    if (!c || !c._drawing) return;
    const ctx = getCanvasCtx();
    const { x, y } = getPos(e);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1C1917";
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  }
  function stopDraw() {
    if (canvasRef.current) canvasRef.current._drawing = false;
  }
  function getPos(e) {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const evt = e.touches?.[0] || e;
    return { x: evt.clientX - rect.left, y: evt.clientY - rect.top };
  }
  function clearCanvas() {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  }

  async function submit() {
    if (!agreed) return toast.error("Please confirm you agree to sign.");
    if (!name.trim() || name.trim().length < 2) return toast.error("Please type your full legal name.");
    setSubmitting(true);
    try {
      const signature_image = hasDrawn ? canvasRef.current.toDataURL("image/png") : "";
      await api.post(`/contracts/sign/${token}`, {
        signature_name: name.trim(),
        signature_image,
      });
      toast.success("Signed — a copy has been emailed to you.");
      // Reload to show signed state
      const { data } = await api.get(`/contracts/sign/${token}`);
      setContract(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit signature");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading contract…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center px-6">
        <div>
          <div className="font-serif text-3xl">Hm.</div>
          <div className="text-sm text-muted-foreground mt-2 max-w-sm">{error}</div>
        </div>
      </div>
    );
  }

  const isSigned = contract?.status === "signed";

  return (
    <div className="min-h-screen bg-background text-foreground py-10 md:py-16" data-testid="sign-contract-page">
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <div className="mb-8 flex items-center justify-between">
          <div className="font-serif text-2xl tracking-[0.18em]">{contract.studio_name}</div>
          <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">{contract.contract_number}</div>
        </div>

        <div className="bg-card border border-border">
          <div className="p-6 md:p-10 border-b border-border">
            <div className="eyebrow mb-2">Agreement</div>
            <h1 className="font-serif text-3xl md:text-4xl font-light leading-tight">{contract.title}</h1>
            <div className="mt-3 text-sm text-muted-foreground">For: <strong className="text-foreground">{contract.customer_name}</strong> · {contract.customer_email}</div>
          </div>

          <div className="p-6 md:p-10 whitespace-pre-wrap text-sm md:text-[15px] leading-relaxed font-serif text-foreground/90" data-testid="contract-body">
            {contract.body}
          </div>

          {isSigned ? (
            <div className="p-6 md:p-10 border-t border-border bg-emerald-50/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-600 text-white flex items-center justify-center"><Check className="w-5 h-5" /></div>
                <div>
                  <div className="font-serif text-xl">Signed</div>
                  <div className="text-xs text-muted-foreground">{contract.signature_name} · {new Date(contract.signed_at).toLocaleString()}</div>
                </div>
              </div>
              {contract.signature_image && (
                <img src={contract.signature_image} alt="Signature" className="mt-5 h-20 bg-white border border-border" />
              )}
              <div className="text-xs text-muted-foreground mt-4">A copy of this signed contract has been emailed to you. Keep it for your records.</div>
            </div>
          ) : (
            <div className="p-6 md:p-10 border-t border-border space-y-6">
              <div>
                <div className="eyebrow mb-2">Type your full legal name *</div>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-transparent border-b border-foreground/40 py-3 outline-none focus:border-foreground text-2xl font-serif italic"
                  placeholder="Your name"
                  data-testid="sign-name-input"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="eyebrow flex items-center gap-2"><PenLine className="w-3.5 h-3.5" /> Draw your signature (optional)</div>
                  {hasDrawn && (
                    <button onClick={clearCanvas} className="text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="clear-signature">
                      <Eraser className="w-3 h-3" /> Clear
                    </button>
                  )}
                </div>
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={160}
                  className="w-full h-40 bg-white border border-border touch-none cursor-crosshair"
                  onMouseDown={startDraw}
                  onMouseMove={draw}
                  onMouseUp={stopDraw}
                  onMouseLeave={stopDraw}
                  onTouchStart={startDraw}
                  onTouchMove={draw}
                  onTouchEnd={stopDraw}
                  data-testid="signature-canvas"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer" data-testid="sign-agree-label">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-foreground"
                  data-testid="sign-agree-checkbox"
                />
                <span className="text-sm">
                  I have read and agree to the terms of this contract. I understand that typing my name and clicking "Sign contract" constitutes my electronic signature, which is legally binding.
                </span>
              </label>

              <button
                onClick={submit}
                disabled={submitting || !agreed || !name.trim()}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 h-12 bg-primary text-primary-foreground text-xs uppercase tracking-[0.22em] hover:bg-primary/90 disabled:opacity-50"
                data-testid="sign-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {submitting ? "Signing…" : "Sign contract"}
              </button>
            </div>
          )}
        </div>

        <div className="text-center text-xs text-muted-foreground mt-8">
          Powered by {contract.studio_name} · Securely signed via {contract.contract_number}
        </div>
      </div>
    </div>
  );
}
